/**
 * Itinerary Data Processor
 * Handles data normalization, validation, and edge case processing for itinerary display
 */

/**
 * Safely parse JSON data with fallback
 */
export const safeJsonParse = (data) => {
  try {
    return typeof data === "string" ? JSON.parse(data) : data;
  } catch (error) {
    console.warn("Failed to parse itinerary data:", error);
    return {};
  }
};

/**
 * Normalize itinerary data structure from various API response formats.
 *
 * FRONTEND CONTRACT (what the UI relies on):
 * - days: array of day plans (from DailyItineraries, dailyItineraries, days, dayPlans, or itinerary).
 * - Each day: date, dayType, parks, timedActivities { morning, afternoon, evening } or timeBlocks;
 *   legacy: morning/afternoon/nighttime strings; dining { lunch, dinner }; notes.
 * - magicMessages: { arrival, departure } (backend-provided preferred; fallbacks used when absent).
 * - tripSummary: guest/trip metadata.
 * - segmentsV2: backend may send segments on each day; timedActivities are adapter-derived from segments.
 * - Zone order is canonical; UI does not reorder activity lists (sort is by time within existing blocks).
 */
export const normalizeItineraryData = (rawData) => {
  if (!rawData) return { days: [], tripSummary: {}, magicMessages: {} };

  let parsedItinerary = {};

  // Handle the exact database structure
  if (rawData.data && rawData.data.itinerary) {
    // Handle nested data structure from API response
    parsedItinerary = rawData.data.itinerary;
  } else if (rawData.itinerarydata) {
    parsedItinerary = rawData.itinerarydata;
  } else if (rawData.itinerary && typeof rawData.itinerary === "object") {
    parsedItinerary = rawData.itinerary;
  } else if (typeof rawData.ItineraryData === "string") {
    parsedItinerary = safeJsonParse(rawData.ItineraryData);
  } else if (typeof rawData.ItineraryData === "object") {
    parsedItinerary = rawData.ItineraryData;
  } else if (rawData.DailyItineraries) {
    parsedItinerary = rawData;
  } else {
    parsedItinerary = rawData;
  }

  // Helper: safely parse nested ItineraryData strings
  const coerceItineraryObject = (value) => {
    if (!value) return null;
    if (typeof value === "string") return safeJsonParse(value);
    if (typeof value === "object") return value;
    return null;
  };

  // Helper: recursively search for the days array (API uses DailyItineraries; segmentsV2 test/fixture may use dayPlans)
  const findDailyItineraries = (obj, depth = 0) => {
    if (!obj || depth > 5) return [];
    const candidates = [
      obj?.DailyItineraries,
      obj?.dailyItineraries,
      obj?.dayPlans,
      obj?.days,
      obj?.itinerary,
    ].filter(Boolean);
    for (const candidate of candidates) {
      if (Array.isArray(candidate) && candidate.length >= 0) {
        return candidate;
      }
    }
    // Explore common nests
    const nested = [
      obj?.data,
      obj?.itinerary,
      obj?.ItineraryData ? coerceItineraryObject(obj.ItineraryData) : null,
      obj?.raw,
      obj?.rawData,
    ].filter(Boolean);
    for (const n of nested) {
      const found = findDailyItineraries(n, depth + 1);
      if (Array.isArray(found)) return found;
    }
    return [];
  };

  // Extract days with robust fallback handling
  let days = findDailyItineraries(parsedItinerary);

  // Validate days array
  if (!Array.isArray(days)) {
    console.warn("Invalid days structure in itinerary data:", days);
    return { days: [], tripSummary: {}, magicMessages: {} };
  }

  // Canonical order: chronological by date so display matches backend and finalParkPreference
  const dateStr = (d) => (d?.date || d?.dateISO || "").toString();
  days = [...days].sort((a, b) => {
    const da = dateStr(a);
    const db = dateStr(b);
    if (!da) return 1;
    if (!db) return -1;
    return da.localeCompare(db);
  });

  // Extract magic messages
  const storedMagic =
    parsedItinerary?.magicMessages ||
    parsedItinerary?.magic ||
    parsedItinerary?.MagicMessages ||
    {};

  // Extract trip summary
  const tripSummary =
    parsedItinerary?.TripSummary || parsedItinerary?.tripSummary || {};

  // Extract questionnaire data
  const questionnaireData =
    parsedItinerary?.questionnaireData ||
    parsedItinerary?.questionnaire ||
    parsedItinerary?.QuestionnaireData ||
    {};

  return {
    days,
    tripSummary,
    magicMessages: {
      arrival: storedMagic.arrival || "Welcome to Disney!",
      departure: storedMagic.departure || "Safe travels!",
    },
    questionnaireData,
    rawData,
  };
};

/**
 * Extract guest information from various data structures
 */
export const extractGuestInfo = (data, tripSummary) => {
  const guestInfo = tripSummary?.GuestInfo || {};

  // Try multiple sources for guest name (including organized structure)
  const guestName =
    tripSummary?.guest_name ||
    tripSummary?.GuestName ||
    guestInfo?.guest_name ||
    guestInfo?.GuestName ||
    data?.clientName ||
    data?.basicInformation?.fullName ||
    data?.fullName ||
    data?.ClientName ||
    data?.FullName ||
    data?.GuestName ||
    data?.guestName ||
    "Guest";

  // Try multiple sources for resort (including organized structure)
  const resort =
    tripSummary?.resort ||
    tripSummary?.Resort ||
    guestInfo?.resort ||
    guestInfo?.Resort ||
    data?.questionnaireData?.accommodation?.resort ||
    data?.accommodation?.resort ||
    data?.parkTicketAccess?.resort ||
    data?.resort ||
    data?.Resort ||
    "Unknown resort";

  return {
    guestName,
    resort,
    email:
      guestInfo?.email ||
      guestInfo?.Email ||
      data?.basicInformation?.email ||
      "",
    phone:
      guestInfo?.phone ||
      guestInfo?.Phone ||
      data?.basicInformation?.phoneNumber ||
      "",
    startDate:
      tripSummary?.TravelDates?.trip_start_date ||
      tripSummary?.startDate ||
      tripSummary?.StartDate ||
      data?.basicInformation?.startDate ||
      data?.startDate ||
      data?.StartDate,
    endDate:
      tripSummary?.TravelDates?.trip_end_date ||
      tripSummary?.endDate ||
      tripSummary?.EndDate ||
      data?.basicInformation?.endDate ||
      data?.endDate ||
      data?.EndDate,
  };
};

/**
 * Validate and clean date strings
 */
export const validateAndFormatDate = (dateString) => {
  if (!dateString)
    return { isValid: false, formatted: "TBD", original: dateString };

  try {
    let date;
    
    // Handle date strings that should be treated as local dates, not UTC
    if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      // For YYYY-MM-DD format, parse as local date to avoid timezone issues
      const [year, month, day] = dateString.split('-').map(Number);
      date = new Date(year, month - 1, day); // month is 0-indexed
    } else {
      date = new Date(dateString);
    }
    
    if (isNaN(date.getTime())) {
      return { isValid: false, formatted: "TBD", original: dateString };
    }

    const formatted = date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });

    return { isValid: true, formatted, original: dateString, dateObject: date };
  } catch (error) {
    console.warn("Date parsing error:", error);
    return { isValid: false, formatted: "TBD", original: dateString };
  }
};

/**
 * Extract arrival time from activity strings
 */
export const extractArrivalTime = (activities) => {
  if (!activities) return null;

  // Handle string activities
  if (typeof activities === "string") {
    const timeMatch = activities.match(
      /(?:Arrive at|arrive at|Arrive|arrive)\s+(\d{1,2}:\d{2}\s*(?:AM|PM|am|pm))/i
    );
    return timeMatch ? timeMatch[1] : null;
  }

  // Handle array activities
  if (Array.isArray(activities) && activities.length > 0) {
    const firstActivity = activities[0];
    if (typeof firstActivity === "string") {
      const timeMatch = firstActivity.match(
        /(?:Arrive at|arrive at|Arrive|arrive)\s+(\d{1,2}:\d{2}\s*(?:AM|PM|am|pm))/i
      );
      return timeMatch ? timeMatch[1] : null;
    }
  }

  return null;
};

/**
 * Clean and normalize activities data
 */
export const cleanActivities = (activities) => {
  if (!activities) return ["No activities listed"];

  // Handle string activities
  if (typeof activities === "string") {
    const parts = activities
      .split(",")
      .map((part) => part.trim())
      .filter((part) => part && part.length > 0);
    return parts.length > 0 ? parts : ["No activities listed"];
  }

  // Handle array activities
  if (Array.isArray(activities)) {
    const cleaned = activities
      .filter(
        (activity) =>
          activity && typeof activity === "string" && activity.trim().length > 0
      )
      .map((activity) => activity.trim());
    return cleaned.length > 0 ? cleaned : ["No activities listed"];
  }

  return ["No activities listed"];
};

/* ---------------------------
 * V2 normalization helpers (QA real output)
 * --------------------------*/

/** Collect all dining activities from timedActivities (all blocks) and segments */
function getDiningActivitiesFromDay(day) {
  const out = [];
  const isDining = (item) => item && typeof item === "object" && item.type === "dining";
  const blocks = day?.timedActivities
    ? [day.timedActivities.morning, day.timedActivities.afternoon, day.timedActivities.evening]
    : [];
  for (const block of blocks) {
    if (!Array.isArray(block)) continue;
    for (const item of block) {
      if (isDining(item)) out.push(item);
    }
  }
  if (day?.segments && Array.isArray(day.segments)) {
    for (const seg of day.segments) {
      const items = seg?.items || [];
      for (const item of items) {
        if (isDining(item)) out.push(item);
      }
    }
  }
  return out;
}

/** Build normalized day.meals: scheduled dining from timedActivities/segments, or fallback to day.lunch/day.dinner */
function normalizeMealsForDay(day) {
  const mealMeta = day?.mealMeta || {};
  const diningActivities = getDiningActivitiesFromDay(day);
  const hasScheduledDining = diningActivities.length > 0;

  const toMinutes = (timeStr) => {
    if (!timeStr || typeof timeStr !== "string") return 0;
    const m = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
    if (!m) return 0;
    let h = parseInt(m[1], 10);
    const min = parseInt(m[2], 10);
    const period = (m[3] || "").toUpperCase();
    if (period === "PM" && h !== 12) h += 12;
    if (period === "AM" && h === 12) h = 0;
    return h * 60 + min;
  };

  if (hasScheduledDining) {
    const sorted = [...diningActivities].sort((a, b) => {
      const tA = toMinutes(a?.time || a?.scheduledTime);
      const tB = toMinutes(b?.time || b?.scheduledTime);
      return tA - tB;
    });
    const lunchCutoff = 15 * 60;
    let scheduledLunchItem = null;
    let scheduledDinnerItem = null;
    for (const item of sorted) {
      const t = toMinutes(item?.time || item?.scheduledTime);
      if (t < lunchCutoff && !scheduledLunchItem) {
        scheduledLunchItem = item;
      } else if (!scheduledDinnerItem) {
        scheduledDinnerItem = item;
      }
    }
    if (!scheduledLunchItem && sorted.length > 0) scheduledLunchItem = sorted[0];
    if (!scheduledDinnerItem && sorted.length > 1) scheduledDinnerItem = sorted[1];
    const lunchName = scheduledLunchItem?.name || "Lunch not specified";
    const dinnerName = scheduledDinnerItem?.name || "Dinner not specified";
    return {
      lunch: lunchName,
      dinner: dinnerName,
      lunchSource: mealMeta.lunchSource ?? null,
      dinnerSource: mealMeta.dinnerSource ?? null,
      scheduledLunchItem: scheduledLunchItem ?? undefined,
      scheduledDinnerItem: scheduledDinnerItem ?? undefined,
    };
  }

  const lunchVal = day?.dining?.lunch ?? day?.lunch;
  const dinnerVal = day?.dining?.dinner ?? day?.dinner;
  const lunchName = typeof lunchVal === "string" ? lunchVal : lunchVal?.name ?? "Lunch not specified";
  const dinnerName = typeof dinnerVal === "string" ? dinnerVal : dinnerVal?.name ?? "Dinner not specified";
  return {
    lunch: lunchName,
    dinner: dinnerName,
    lunchSource: mealMeta.lunchSource ?? null,
    dinnerSource: mealMeta.dinnerSource ?? null,
    scheduledLunchItem: undefined,
    scheduledDinnerItem: undefined,
  };
}

/** Normalize any timedActivities item whose name starts with "Transfer to" to type="transfer" */
function normalizeTransferTypeInTimedActivities(timedActivities) {
  if (!timedActivities || typeof timedActivities !== "object") return timedActivities;
  const result = {};
  for (const block of ["morning", "afternoon", "evening"]) {
    const arr = timedActivities[block];
    if (!Array.isArray(arr)) {
      result[block] = arr;
      continue;
    }
    result[block] = arr.map((item) => {
      if (!item || typeof item !== "object") return item;
      const name = item.name || "";
      if (typeof name === "string" && name.trim().toLowerCase().startsWith("transfer to")) {
        return { ...item, type: "transfer" };
      }
      return item;
    });
  }
  return result;
}

/** Dedupe overflow list in notes: detect "Could not schedule (overflow):" section, split by comma, trim, unique (case-insensitive), rejoin */
export function dedupeOverflowNotes(notes) {
  if (notes == null) return notes;
  const raw = Array.isArray(notes) ? notes.join("\n") : String(notes);
  const overflowMarker = "Could not schedule (overflow):";
  const idx = raw.indexOf(overflowMarker);
  if (idx === -1) return notes;

  const before = raw.slice(0, idx + overflowMarker.length);
  const listPart = raw.slice(idx + overflowMarker.length).trimLeft();
  const items = listPart.split(",").map((s) => s.replace(/\s+/g, " ").trim()).filter(Boolean);
  const seen = new Set();
  const deduped = items.filter((item) => {
    const key = item.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  const result = before + (deduped.length ? " " + deduped.join(", ") : " " + listPart);
  return Array.isArray(notes) ? [result] : result;
}

/**
 * Transform new backend data structure to frontend-compatible format
 * Applies V2 normalization: meals source-of-truth, transfer type, overflow dedupe.
 */
export const transformItineraryData = (itineraryData) => {
  if (!itineraryData || !Array.isArray(itineraryData)) {
    return itineraryData;
  }

  return itineraryData.map(day => {
    // Legacy: already in old format (no timeBlocks, has morning/afternoon strings)
    if (!day.timeBlocks && (day.morning || day.afternoon)) {
      const legacyDay = { ...day };
      legacyDay.notes = dedupeOverflowNotes(day.notes ?? []);
      return legacyDay;
    }

    // Build timedActivities from timeBlocks or use existing
    const timedActivitiesRaw = day.timeBlocks
      ? {
          morning: day.timeBlocks?.morning?.activities || [],
          afternoon: day.timeBlocks?.afternoon?.activities || [],
          evening: day.timeBlocks?.evening?.activities || [],
        }
      : (day.timedActivities || { morning: [], afternoon: [], evening: [] });

    const timedActivities = normalizeTransferTypeInTimedActivities(timedActivitiesRaw);

    const transformedDay = {
      ...day,
      morning: (timedActivities.morning || []).map((a) => (typeof a === "object" ? a?.name : a)).filter(Boolean),
      afternoon: (timedActivities.afternoon || []).map((a) => (typeof a === "object" ? a?.name : a)).filter(Boolean),
      nighttime: (timedActivities.evening || []).map((a) => (typeof a === "object" ? a?.name : a)).filter(Boolean),
      lunch: day.dining?.lunch ?? day.lunch,
      dinner: day.dining?.dinner ?? day.dinner,
      date: day.date,
      dayType: day.dayType,
      parks: day.parks,
      notes: dedupeOverflowNotes(day.notes ?? []),
      timedActivities,
      isSprings: day.isSprings,
      isParkHopper: day.isParkHopper,
      isArrivalDay: day.isArrivalDay,
      isDepartureDay: day.isDepartureDay,
    };

    transformedDay.meals = normalizeMealsForDay({ ...day, timedActivities });
    return transformedDay;
  });
};

/**
 * Determine day type and characteristics
 * Updated to work with new backend data structure
 */
export const analyzeDayType = (dayInfo) => {
  // Handle null/undefined dayInfo
  if (!dayInfo) {
    return {
      isArrival: false,
      isDeparture: false,
      isRest: false,
      isSprings: false,
      isParkHopper: false,
      isPark: false,
      hopperParks: [],
      dayLabel: "Unknown Day",
    };
  }

  // Check for new structure first (dayType field)
  if (dayInfo.dayType) {
    const isArrival = dayInfo.dayType === "arrival";
    const isDeparture = dayInfo.dayType === "departure";
    const isParkHopper = dayInfo.dayType === "hopper";
    const isPark = dayInfo.dayType === "park";
    
    // Check for Disney Springs using the isSprings field from backend
    const isSprings = dayInfo.isSprings === true;
    const isRest = isSprings; // Disney Springs days are treated as rest days

    return {
      isArrival,
      isDeparture,
      isRest,
      isSprings,
      isParkHopper,
      isPark,
      hopperParks: isParkHopper ? dayInfo.parks || [] : [],
      dayLabel: getDayLabel(dayInfo),
    };
  }

  // Fallback to legacy structure
  const activity = (dayInfo.activity || "").toLowerCase();
  const parkData = dayInfo.park || dayInfo.Park;

  // Check for explicit arrival/departure day labels
  const isArrival = parkData === "Arrival Day" || activity.includes("arrival");
  const isDeparture =
    parkData === "Departure Day" || activity.includes("departure");
  const isRest = activity.includes("rest");
  const isSprings =
    (dayInfo.flex && dayInfo.flex === "Disney Springs") ||
    activity.includes("disney springs");

  // Determine if it's a park hopper day
  const isParkHopper = Array.isArray(parkData) && parkData.length > 1;

  // Get hopper parks
  let hopperParks = [];
  if (isParkHopper) {
    hopperParks = parkData;
  } else if (dayInfo.nighttime && dayInfo.nighttime.startsWith("Evening at ")) {
    const match = dayInfo.nighttime.match(/^Evening at ([^:]+):/);
    if (match && dayInfo.park) {
      hopperParks = [dayInfo.park, match[1]];
    }
  }

  return {
    isArrival,
    isDeparture,
    isRest,
    isSprings,
    isParkHopper,
    isPark: !isArrival && !isDeparture && !isRest && !isSprings,
    hopperParks,
    dayLabel: getDayLabel(dayInfo),
  };
};

/**
 * Generate day label based on day information
 * Updated to work with new backend data structure
 */
const getDayLabel = (info) => {
  // Check for new structure first
  if (info.dayType) {
    switch (info.dayType) {
      case "arrival":
        return "Arrival Day";
      case "departure":
        return "Departure Day";
      case "hopper":
        return info.parks ? info.parks.join(" + ") : "Park Hopper";
      case "park":
        return info.parks && info.parks.length > 0 ? info.parks[0] : "Park Day";
      default:
        return "Unknown Day";
    }
  }

  // Fallback to legacy structure
  const activity = (info.activity || "").toLowerCase();
  const parkData = info.park || info.Park;

  // Check for explicit arrival/departure day labels
  if (parkData === "Arrival Day") return "Arrival Day";
  if (parkData === "Departure Day") return "Departure Day";

  // Legacy pattern matching
  if (activity.includes("arrival")) return "Arrival Day";
  if (activity.includes("departure")) return "Departure Day";
  if (activity.includes("rest")) return "Rest Day";

  if (Array.isArray(parkData) && parkData.length > 0) {
    return parkData.join(" + "); // Park Hopper
  }

  if (typeof parkData === "string" && parkData.trim()) {
    return parkData;
  }

  if (typeof parkData === "object" && parkData?.name) {
    return parkData.name;
  }

  return "No Park Day";
};

/**
 * Get dining information from new or legacy structure.
 * When day.meals exists (V2 normalized), use it as source of truth to avoid duplicate meal display.
 * Badge precedence: 1) day.meals (normalized), 2) day.dining.{lunch|dinner}.clientSelected, 3) day.mealMeta.{lunchSource|dinnerSource} === 'client'.
 */
export const getDiningInfo = (day) => {
  if (!day) {
    return {
      lunch: "Lunch not specified",
      dinner: "Dinner not specified",
    };
  }

  // 0) V2 normalized: day.meals is source of truth (scheduled dining from timedActivities/segments or fallback)
  if (day.meals && typeof day.meals === "object") {
    return {
      lunch: day.meals.lunch || "Lunch not specified",
      dinner: day.meals.dinner || "Dinner not specified",
      lunchType: day.meals.scheduledLunchItem?.type ?? null,
      dinnerType: day.meals.scheduledDinnerItem?.type ?? null,
      lunchClientSelected: day.meals.lunchSource === "client",
      dinnerClientSelected: day.meals.dinnerSource === "client",
    };
  }

  // 1) Prefer day.dining for correct Client/System Choice badges; fall back to top-level day.lunch/day.dinner when dining has nulls (segmentsV2 overflow case)
  // Client Choice if: item.choiceSource === "client" OR item.__fromMustTry === true OR mealMeta.lunchSource/dinnerSource === "client"
  if (day.dining) {
    const lunchFromDining = typeof day.dining.lunch === "string" ? day.dining.lunch : day.dining.lunch?.name;
    const dinnerFromDining = typeof day.dining.dinner === "string" ? day.dining.dinner : day.dining.dinner?.name;
    const lunchName = lunchFromDining || (typeof day.lunch === "string" ? day.lunch : day.lunch?.name) || "Lunch not specified";
    const dinnerName = dinnerFromDining || (typeof day.dinner === "string" ? day.dinner : day.dinner?.name) || "Dinner not specified";
    let lunchClientSelected = day.dining.lunch?.clientSelected === true;
    let dinnerClientSelected = day.dining.dinner?.clientSelected === true;
    if (!lunchClientSelected || !dinnerClientSelected) {
      const timed = day.timedActivities;
      if (timed && (timed.morning || timed.afternoon || timed.evening)) {
        const blocks = [timed.morning, timed.afternoon, timed.evening].filter(Array.isArray);
        for (const block of blocks) {
          for (const item of block) {
            if (item && item.type === "dining") {
              const isClient = item.choiceSource === "client" || item.__fromMustTry === true;
              if (item.mealType === "lunch") lunchClientSelected = lunchClientSelected || isClient;
              if (item.mealType === "dinner") dinnerClientSelected = dinnerClientSelected || isClient;
            }
          }
        }
      }
    }
    if (!lunchClientSelected && day.mealMeta?.lunchSource === "client") lunchClientSelected = true;
    if (!dinnerClientSelected && day.mealMeta?.dinnerSource === "client") dinnerClientSelected = true;
    return {
      lunch: lunchName,
      dinner: dinnerName,
      lunchType: day.dining.lunch?.type ?? null,
      dinnerType: day.dining.dinner?.type ?? null,
      lunchClientSelected,
      dinnerClientSelected,
    };
  }

  // 2) Fallback: day.mealMeta for badges when day.dining absent
  if (day.lunch || day.dinner) {
    return {
      lunch: day.lunch || "Lunch not specified",
      dinner: day.dinner || "Dinner not specified",
      lunchType: null,
      dinnerType: null,
      lunchClientSelected: day.mealMeta?.lunchSource === "client",
      dinnerClientSelected: day.mealMeta?.dinnerSource === "client",
    };
  }

  // Legacy: meals array
  if (day.meals && Array.isArray(day.meals)) {
    return {
      lunch: day.meals[0] || "Lunch not specified",
      dinner: day.meals[1] || "Dinner not specified",
      lunchType: null,
      dinnerType: null,
      lunchClientSelected: false,
      dinnerClientSelected: false,
    };
  }

  // Legacy: individual lunch/dinner without dining or mealMeta — badge unknown, treat as system and warn
  if (typeof process !== "undefined" && process.env?.NODE_ENV === "development") {
    console.warn("[getDiningInfo] Day has lunch/dinner but no day.dining or day.mealMeta; Client/System badge may be incorrect.", { hasLunch: !!day.lunch, hasDinner: !!day.dinner });
  }
  return {
    lunch: typeof day.lunch === "string" ? day.lunch : day.lunch?.name || "Lunch not specified",
    dinner: typeof day.dinner === "string" ? day.dinner : day.dinner?.name || "Dinner not specified",
    lunchType: day.lunch?.type || null,
    dinnerType: day.dinner?.type || null,
    lunchClientSelected: false,
    dinnerClientSelected: false,
  };
};

/**
 * Get activities from new timeBlocks structure or legacy structure
 */
export const getActivitiesByTimeBlock = (day) => {

  // Handle null/undefined day
  if (!day) {
    return {
      morning: [],
      afternoon: [],
      evening: [],
    };
  }

  // Helper: classify a single text snippet into a typed activity
  const classifyTextActivity = (text) => {
    if (!text || typeof text !== 'string') return null;
    const trimmed = text.trim();
    if (!trimmed) return null;
    // Transfer notes should not count as rides/shows
    if (/^transfer to/i.test(trimmed)) {
      return {
        type: 'note',
        name: trimmed,
        isTransfer: true,
      };
    }
    const timeMatch = trimmed.match(/(\d{1,2}:\d{2}\s?(?:AM|PM))/i);
    const name = trimmed
      .replace(/\(\d+\s?minutes\)/ig, '')
      .replace(/-\s?\d+\s?minutes/ig, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
    const lower = name.toLowerCase();
    const isDining = /\b(lunch|dinner|breakfast|snack)\b/.test(lower);
    // Expanded show keywords to cover common WDW entertainment names
    const isShow = /\b(show|parade|firework|stage|spectacular|awakenings)\b|fantasmic|harmonious|luminous|starlight|festival|faire|friendship\s+faire|dream\s+the\s+night\s+away|beauty\s+and\s+the\s+beast/i.test(lower);
    
    // For structured activities from timedActivities, preserve the original type
    // This handles Disney Springs activities with types like 'retail', 'attraction', 'unique_experience', etc.
    let type = isDining ? 'dining' : (isShow ? 'show' : 'ride');
    
    // If this is coming from a structured activity object, preserve its type
    if (typeof text === 'object' && text.type) {
      type = text.type;
    }
    return {
      type,
      name,
      time: timeMatch ? timeMatch[1].toUpperCase() : undefined,
    };
  };

  // Helper: parse a legacy string like "MK: Ride A (10:00 AM) - 20 minutes, Ride B ..."
  const parseLegacyActivities = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value !== 'string') return [];
    // Split on commas that separate activities
    const parts = value.split(',').map(s => s.trim()).filter(Boolean);
    const activities = parts
      .map(classifyTextActivity)
      .filter(Boolean);
    return activities;
  };

  // New structure with timeBlocks
  if (day.timeBlocks) {
    const result = {
      morning: day.timeBlocks.morning?.activities || [],
      afternoon: day.timeBlocks.afternoon?.activities || [],
      evening: day.timeBlocks.evening?.activities || [],
    };
    return result;
  }

  // segmentsV2: when only segments exist (no timeBlocks/timedActivities), derive morning/afternoon/evening from segment items
  if (day.segments && Array.isArray(day.segments) && day.segments.length > 0) {
    const morning = [];
    const afternoon = [];
    const evening = [];
    const toMinutes = (timeStr) => {
      if (!timeStr || typeof timeStr !== "string") return 0;
      const m = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
      if (!m) return 0;
      let h = parseInt(m[1], 10);
      const min = parseInt(m[2], 10);
      const period = (m[3] || "").toUpperCase();
      if (period === "PM" && h !== 12) h += 12;
      if (period === "AM" && h === 12) h = 0;
      return h * 60 + min;
    };
    for (const seg of day.segments) {
      const items = seg?.items || [];
      for (const item of items) {
        const timeStr = typeof item === "object" ? item?.time : null;
        const t = typeof item === "object" && item?.startMin != null
          ? item.startMin
          : (timeStr ? toMinutes(timeStr) : 0);
        const name = typeof item === "string" ? item : (item?.name || "Activity");
        const type = (typeof item === "object" && item?.type) ? item.type : "ride";
        const act = typeof item === "object"
          ? { ...item, name: name || item.name, type, time: timeStr || item.time }
          : { type: "ride", name, time: timeStr };
        if (t < 12 * 60) morning.push(act);
        else if (t < 17 * 60) afternoon.push(act);
        else evening.push(act);
      }
    }
    const hasAny = morning.length + afternoon.length + evening.length > 0;
    if (hasAny) return { morning, afternoon, evening };
  }

  // Enhanced structure with timed activities (direct arrays)
  if (day.timedActivities) {
    const blocks = {
      morning: day.timedActivities.morning || [],
      afternoon: day.timedActivities.afternoon || [],
      evening: day.timedActivities.evening || [],
    };
    const hasAny = (blocks.morning?.length || 0) + (blocks.afternoon?.length || 0) + (blocks.evening?.length || 0) > 0;
    if (hasAny) return blocks;
    // Fallback: parse legacy string fields if timedActivities exist but are empty
    return {
      morning: parseLegacyActivities(day.morning),
      afternoon: parseLegacyActivities(day.afternoon),
      evening: parseLegacyActivities(day.nighttime),
    };
  }

  // Legacy structure
  return {
    morning: parseLegacyActivities(day.morning || []),
    afternoon: parseLegacyActivities(day.afternoon || []),
    evening: parseLegacyActivities(day.nighttime || []),
  };
};

/**
 * Get all rides from new or legacy structure
 */
export const getAllRides = (day) => {

  // Handle null/undefined day
  if (!day) {
    return [];
  }

  const blocks = getActivitiesByTimeBlock(day);
  const allActivities = [
    ...(blocks.morning || []),
    ...(blocks.afternoon || []),
    ...(blocks.evening || []),
  ];
  return allActivities
    .filter((activity) => (activity && typeof activity === 'object' && 
      (activity.type === 'ride' || 
       activity.type === 'attraction' || 
       activity.type === 'family_ride' ||
       activity.type === 'unique_experience')))
    .map((activity) => activity.name || activity);
};

/**
 * Get all shows from new or legacy structure
 */
export const getAllShows = (day) => {

  // Handle null/undefined day
  if (!day) {
    return [];
  }

  const blocks = getActivitiesByTimeBlock(day);
  const allActivities = [
    ...(blocks.morning || []),
    ...(blocks.afternoon || []),
    ...(blocks.evening || []),
  ];
  return allActivities
    .filter((activity) => (activity && typeof activity === 'object' && 
      (activity.type === 'show' || 
       activity.type === 'live_show')))
    .map((activity) => activity.name || activity);
};

/**
 * Get all retail/shopping activities (for Disney Springs)
 */
export const getAllRetail = (day) => {

  // Handle null/undefined day
  if (!day) {
    return [];
  }

  const blocks = getActivitiesByTimeBlock(day);
  const allActivities = [
    ...(blocks.morning || []),
    ...(blocks.afternoon || []),
    ...(blocks.evening || []),
  ];
  return allActivities
    .filter((activity) => (activity && typeof activity === 'object' && 
      activity.type === 'retail'))
    .map((activity) => activity.name || activity);
};

/**
 * Check if day has dining (works with new and legacy structures)
 */
export const hasDiningInfo = (day) => {
  if (!day) return false;

  try {
    const diningInfo = getDiningInfo(day);
    
    // Check for generic/placeholder dining options that shouldn't count as real dining
    const isGenericDining = (dining) => {
      if (!dining) return true;
      const genericPatterns = [
        "Lunch not specified",
        "Dinner not specified", 
        "Quick service at the water park",
        "Casual dining at the water park or return to resort",
        "Dinner at your resort or Disney Springs"
      ];
      return genericPatterns.some(pattern => 
        dining.toLowerCase().includes(pattern.toLowerCase())
      );
    };
    
    return (
      !!(diningInfo.lunch && !isGenericDining(diningInfo.lunch)) ||
      !!(diningInfo.dinner && !isGenericDining(diningInfo.dinner))
    );
  } catch (error) {
    console.warn("Error checking dining info:", error);
    return false;
  }
};

/**
 * Get specialty experiences from day data (works with new and legacy structures)
 */
export const getSpecialtyExperiences = (day) => {
  // Handle null/undefined day
  if (!day) {
    return [];
  }

  // New structure with specialtyExperiences object
  if (day.specialtyExperiences) {
    return day.specialtyExperiences.experiences || [];
  }

  // Legacy structure with experiences array
  if (day.experiences && Array.isArray(day.experiences)) {
    return day.experiences;
  }

  return [];
};

/**
 * Get all specialty experiences from itinerary data
 */
export const getAllSpecialtyExperiences = (data) => {
  const experiences = [];

  if (!data || !data.DailyItineraries) return experiences;

  data.DailyItineraries.forEach((day) => {
    const dayExperiences = getSpecialtyExperiences(day);
    experiences.push(...dayExperiences);
  });

  return experiences;
};

/**
 * Check if day has specialty experiences
 */
export const hasSpecialtyExperiences = (day) => {
  const experiences = getSpecialtyExperiences(day);
  return experiences.length > 0;
};

/**
 * Validate itinerary data structure
 */
export const validateItineraryData = (data) => {
  const errors = [];
  const warnings = [];

  if (!data) {
    errors.push("No itinerary data provided");
    return { isValid: false, errors, warnings };
  }

  const normalized = normalizeItineraryData(data);

  if (normalized.days.length === 0) {
    errors.push("No days found in itinerary");
  }

  // Check for malformed day data
  normalized.days.forEach((day, index) => {
    if (!day || typeof day !== "object") {
      errors.push(`Day ${index + 1} is malformed`);
      return;
    }

    // Check for required fields
    if (!day.park && !day.activity && !day.flex) {
      warnings.push(`Day ${index + 1} has no park or activity specified`);
    }

    // Check for valid date
    if (day.date) {
      const dateValidation = validateAndFormatDate(day.date);
      if (!dateValidation.isValid) {
        warnings.push(`Day ${index + 1} has invalid date: ${day.date}`);
      }
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    normalized,
  };
};

/* ---------------------------
 * Helpers for Agent Notes
 * --------------------------*/

/** Shared phone formatter */
const formatPhoneNumber = (phoneStr) => {
  if (!phoneStr) return "";
  const digits = String(phoneStr).replace(/\D/g, "");
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits[0] === "1") {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return phoneStr;
};

const _currencyRange = (min, max) => {
  const f = (n) => (typeof n === "number" ? `$${n.toLocaleString()}` : null);
  if (min && max) return `${f(min)} – ${f(max)}`;
  if (min) return `${f(min)}+`;
  if (max) return `Up to ${f(max)}`;
  return null;
};

const compact = (arr) => arr.filter(Boolean);

/**
 * Process comprehensive agent notes from itinerary data
 * Returns organized sections for display in the Agent Notes modal
 * Based on the key sections needed for agent coordination
 */
export const processAgentNotes = (itinerary) => {
  if (!itinerary) return { sections: [], summary: "" };

  // Questionnaire answers: top-level, nested, or from TripSummary.Preferences (Prod/QA same shape)
  const q = itinerary?.questionnaireData ?? itinerary?.QuestionnaireData ?? itinerary?.tripSummary?.Preferences ?? {};
  const ts = itinerary?.tripSummary || itinerary?.TripSummary || {};
  const gi = ts?.GuestInfo || {};
  const travelDates = ts?.TravelDates || {};
  const acc = q?.accommodation || {};
  const pta = q?.parkTicketAccess || {};
  const basic = q?.basicInformation || {};

  const sections = [];

  // SECTION 1: Trip Basics
  const start = validateAndFormatDate(
    travelDates?.trip_start_date || basic?.startDate
  )?.formatted;
  const end = validateAndFormatDate(
    travelDates?.trip_end_date || basic?.endDate
  )?.formatted;

  const clientName =
    gi?.guest_name || basic?.fullName || itinerary?.clientName || "Guest";
  const email =
    gi?.email ||
    basic?.email ||
    itinerary?.email ||
    itinerary?.guestEmail ||
    "";
  const phone =
    gi?.phone ||
    basic?.phoneNumber ||
    itinerary?.phone ||
    itinerary?.guestPhone ||
    "";
  const resort =
    ts?.Resort || ts?.resort || acc?.resort || itinerary?.resort || "—";
  const transport = acc?.transportation || itinerary?.transportation || "—";
  const ticketType = pta?.ticketType || "—";
  const parkHopper = pta?.hasParkHopper || "No";
  const specialEvents = pta?.specialEvents || "—";

  // Group size
  const g = q?.groupInfo || ts?.GroupInfo || {};
  const groupBits = [];
  if (g?.adults && g.adults !== "0") groupBits.push(`${g.adults} adults`);
  if (g?.kids && g.kids !== "0") groupBits.push(`${g.kids} children`);
  const groupSize = groupBits.length ? groupBits.join(", ") : "—";

  // Trip duration calculation
  const tripDuration =
    start && end
      ? Math.ceil((new Date(end) - new Date(start)) / (1000 * 60 * 60 * 24)) + 1
      : "—";

  sections.push({
    title: "Trip Basics",
    subtitle: "Anchor details for logistics coordination",
    items: [
      { label: "Client Name", value: clientName, highlight: true },
      {
        label: "Travel Dates",
        value: start && end ? `${start} – ${end}` : "—",
        highlight: true,
      },
      {
        label: "Trip Duration",
        value: `${tripDuration} days`,
        highlight: true,
      },
      { label: "Resort", value: resort, highlight: true },
      { label: "Transportation", value: transport },
      { label: "Ticket Type", value: ticketType },
      { label: "Park Hopper", value: parkHopper },
      { label: "Special Events", value: specialEvents },
      { label: "Group Size", value: groupSize },
      ...(email ? [{ label: "Email", value: email }] : []),
      ...(phone ? [{ label: "Phone", value: formatPhoneNumber(phone) }] : []),
    ],
  });

  // SECTION 2: Dining Notes
  const dp = q?.diningPreferences || {};
  const diningPlan = dp?.diningPlanInterest || "No";
  const diningTime =
    Array.isArray(dp?.diningTime) && dp.diningTime.length
      ? dp.diningTime.join(", ")
      : "—";

  // Extract client-selected restaurants with priority indicators
  const selectedDining = [];
  const diningByPark = { MK: [], EPCOT: [], HS: [], AK: [] };

  // First, get restaurants from daily itineraries (client-selected)
  (itinerary?.DailyItineraries || []).forEach((day) => {
    if (day?.dining?.lunch?.clientSelected) {
      selectedDining.push(day.dining.lunch.name);
      // Categorize by park for better organization
      if (day.parks && day.parks[0]) {
        const park = day.parks[0];
        if (diningByPark[park]) {
          diningByPark[park].push(day.dining.lunch.name);
        }
      }
    }
    if (day?.dining?.dinner?.clientSelected) {
      selectedDining.push(day.dining.dinner.name);
      // Categorize by park for better organization
      if (day.parks && day.parks[0]) {
        const park = day.parks[0];
        if (diningByPark[park]) {
          diningByPark[park].push(day.dining.dinner.name);
        }
      }
    }
  });

  // Also include mustTryRestaurants from questionnaire data
  if (Array.isArray(dp?.mustTryRestaurants) && dp.mustTryRestaurants.length > 0) {
    dp.mustTryRestaurants.forEach((restaurant) => {
      if (!selectedDining.includes(restaurant)) {
        selectedDining.push(restaurant);
        // Enhanced restaurant categorization by name patterns
        const restaurantLower = restaurant.toLowerCase();
        
        // Magic Kingdom restaurants
        if (restaurantLower.includes('magic kingdom') || 
            restaurantLower.includes('mk') ||
            restaurantLower.includes('be our guest') ||
            restaurantLower.includes('cinderella') ||
            restaurantLower.includes('liberty tree') ||
            restaurantLower.includes('tony') ||
            restaurantLower.includes('crystal palace') ||
            restaurantLower.includes('plaza') ||
            restaurantLower.includes('casey') ||
            restaurantLower.includes('cosmic ray')) {
          diningByPark.MK.push(restaurant);
        }
        // EPCOT restaurants
        else if (restaurantLower.includes('epcot') || 
                 restaurantLower.includes('ep') ||
                 restaurantLower.includes('le cellier') ||
                 restaurantLower.includes('teppan edo') ||
                 restaurantLower.includes('rose & crown') ||
                 restaurantLower.includes('spice road') ||
                 restaurantLower.includes('coral reef') ||
                 restaurantLower.includes('garden grill') ||
                 restaurantLower.includes('akershus') ||
                 restaurantLower.includes('san angel')) {
          diningByPark.EPCOT.push(restaurant);
        }
        // Hollywood Studios restaurants
        else if (restaurantLower.includes('hollywood') || 
                 restaurantLower.includes('studios') || 
                 restaurantLower.includes('hs') ||
                 restaurantLower.includes('sci-fi') ||
                 restaurantLower.includes('50s prime time') ||
                 restaurantLower.includes('hollywood & vine') ||
                 restaurantLower.includes('mama melrose') ||
                 restaurantLower.includes('brown derby') ||
                 restaurantLower.includes('docking bay')) {
          diningByPark.HS.push(restaurant);
        }
        // Animal Kingdom restaurants
        else if (restaurantLower.includes('animal kingdom') || 
                 restaurantLower.includes('ak') ||
                 restaurantLower.includes('tusker house') ||
                 restaurantLower.includes('yak & yeti') ||
                 restaurantLower.includes('satu\'li') ||
                 restaurantLower.includes('flame tree') ||
                 restaurantLower.includes('pizzafari') ||
                 restaurantLower.includes('rainforest') ||
                 restaurantLower.includes('tiffins')) {
          diningByPark.AK.push(restaurant);
        }
        // Disney Springs restaurants
        else if (restaurantLower.includes('disney springs') || 
                 restaurantLower.includes('springs') ||
                 restaurantLower.includes('homecomin') ||
                 restaurantLower.includes('wine bar') ||
                 restaurantLower.includes('boathouse') ||
                 restaurantLower.includes('morimoto') ||
                 restaurantLower.includes('raglan road')) {
          // Add Disney Springs to a separate category or to MK as default
          diningByPark.MK.push(restaurant);
        }
        else {
          // If we can't categorize, add to Magic Kingdom as default
          diningByPark.MK.push(restaurant);
        }
      }
    });
  }

  // Process restaurant data

  // High-priority dining (requires 60-day advance booking)
  const highPriorityDining = [
    "Be Our Guest Restaurant",
    "Cinderella's Royal Table",
    "The Hollywood Brown Derby",
    "Akershus Royal Banquet Hall",
    "Space 220 Restaurant",
  ];
  const urgentBookings = selectedDining.filter((name) =>
    highPriorityDining.some((priority) => name.includes(priority))
  );

  const allergies = dp?.allergies;
  const foodAllergies =
    Array.isArray(allergies) && allergies.length && !allergies.includes("None")
      ? allergies.join(", ")
      : "None";

  sections.push({
    title: "Dining Notes",
    subtitle: "Booking priorities and preferences",
    items: [
      { label: "Dining Plan", value: diningPlan, highlight: true },
      { label: "Dining Time Preference", value: diningTime },
      { label: "Food Allergies", value: foodAllergies },
      ...(urgentBookings.length
        ? [
            {
              label: "High-Priority Bookings (60-day advance)",
              value: urgentBookings.join(", "),
              highlight: true,
              urgent: true,
            },
          ]
        : []),
      ...(Object.values(diningByPark).some(park => park.length > 0)
        ? [
            {
              label: "Selected Restaurants",
              value: `${Object.values(diningByPark).flat().length} restaurants selected`,
              hasDetailView: true,
              detailType: "restaurants",
              detailData: diningByPark,
            },
          ]
        : []),
    ],
  });

  // SECTION 3: Park Priorities
  const pvp = q?.parkVisitPlans || {};
  const parkAllocation = compact([
    pvp?.mkDays ? `MK: ${pvp.mkDays} days` : null,
    pvp?.epcotDays ? `EPCOT: ${pvp.epcotDays} days` : null,
    pvp?.hsDays ? `HS: ${pvp.hsDays} days` : null,
    pvp?.akDays ? `AK: ${pvp.akDays} days` : null,
  ]).join("  •  ");

  const rideStrat = q?.attractionRideStrategy || {};
  const mustRides = Array.isArray(rideStrat?.mustRides)
    ? rideStrat.mustRides
    : [];
  const rideStrategy = Array.isArray(rideStrat?.rideStrategy)
    ? rideStrat.rideStrategy.join(", ")
    : rideStrat?.rideStrategy || "—";
  const lightningLane = rideStrat?.lightningLane || "No";

  // Hard-to-get rides requiring Virtual Queue or Lightning Lane
  const virtualQueueRides = [
    "TRON Lightcycle / Run",
    "Guardians of the Galaxy: Cosmic Rewind",
    "Star Wars: Rise of the Resistance",
  ];
  const lightningLaneRides = [
    "Seven Dwarfs Mine Train",
    "Remy's Ratatouille Adventure",
    "Frozen Ever After",
    "Avatar Flight of Passage",
  ];

  const vqRides = mustRides.filter((r) => virtualQueueRides.includes(r));
  const llRides = mustRides.filter((r) => lightningLaneRides.includes(r));

  // Organize rides by park for better agent reference
  const ridesByPark = { MK: [], EPCOT: [], HS: [], AK: [] };
  mustRides.forEach((ride) => {
    // Magic Kingdom rides
    if (
      ride.includes("TRON") ||
      ride.includes("Space Mountain") ||
      ride.includes("Seven Dwarfs") ||
      ride.includes("Big Thunder") ||
      ride.includes("Splash Mountain") ||
      ride.includes("Jungle Cruise") ||
      ride.includes("Pirates of the Caribbean") ||
      ride.includes("Haunted Mansion") ||
      ride.includes("It's a Small World") ||
      ride.includes("Peter Pan") ||
      ride.includes("Dumbo") ||
      ride.includes("Mad Tea Party") ||
      ride.includes("Buzz Lightyear") ||
      ride.includes("Astro Orbiter") ||
      ride.includes("Tomorrowland") ||
      ride.includes("Adventureland") ||
      ride.includes("Fantasyland") ||
      ride.includes("Frontierland") ||
      ride.includes("Liberty Square")
    ) {
      ridesByPark.MK.push(ride);
    }
    // EPCOT rides
    else if (
      ride.includes("Guardians") ||
      ride.includes("Frozen") ||
      ride.includes("Test Track") ||
      ride.includes("Soarin") ||
      ride.includes("Spaceship Earth") ||
      ride.includes("Mission: SPACE") ||
      ride.includes("Living with the Land") ||
      ride.includes("Journey Into Imagination") ||
      ride.includes("The Seas") ||
      ride.includes("Gran Fiesta") ||
      ride.includes("Remy") ||
      ride.includes("Ratatouille")
    ) {
      ridesByPark.EPCOT.push(ride);
    }
    // Hollywood Studios rides
    else if (
      ride.includes("Rise of the Resistance") ||
      ride.includes("Slinky Dog") ||
      ride.includes("Tower of Terror") ||
      ride.includes("Rock 'n' Roller") ||
      ride.includes("Toy Story") ||
      ride.includes("Mickey") ||
      ride.includes("Minnie") ||
      ride.includes("Star Wars") ||
      ride.includes("Millennium Falcon") ||
      ride.includes("Alien") ||
      ride.includes("Muppet") ||
      ride.includes("Indiana Jones") ||
      ride.includes("Fantasmic")
    ) {
      ridesByPark.HS.push(ride);
    }
    // Animal Kingdom rides
    else if (
      ride.includes("Flight of Passage") ||
      ride.includes("Expedition Everest") ||
      ride.includes("Kilimanjaro") ||
      ride.includes("Dinosaur") ||
      ride.includes("Primeval Whirl") ||
      ride.includes("Kali River") ||
      ride.includes("Maharajah") ||
      ride.includes("TriceraTop") ||
      ride.includes("Na'vi River") ||
      ride.includes("It's Tough to be a Bug") ||
      ride.includes("Festival of the Lion King") ||
      ride.includes("Finding Nemo") ||
      ride.includes("Pandora")
    ) {
      ridesByPark.AK.push(ride);
    }
    // If we can't categorize, add to a general list or try to categorize by common keywords
    else {
      // Try to categorize by park names in the ride name
      if (ride.toLowerCase().includes("magic kingdom") || ride.toLowerCase().includes("mk")) {
        ridesByPark.MK.push(ride);
      } else if (ride.toLowerCase().includes("epcot") || ride.toLowerCase().includes("ep")) {
        ridesByPark.EPCOT.push(ride);
      } else if (ride.toLowerCase().includes("hollywood") || ride.toLowerCase().includes("studios") || ride.toLowerCase().includes("hs")) {
        ridesByPark.HS.push(ride);
      } else if (ride.toLowerCase().includes("animal kingdom") || ride.toLowerCase().includes("ak")) {
        ridesByPark.AK.push(ride);
      } else {
        // If we still can't categorize, add to Magic Kingdom as default
        ridesByPark.MK.push(ride);
      }
    }
  });

  sections.push({
    title: "Park Priorities",
    subtitle: "Ride strategy and high-demand attractions",
    items: [
      { label: "Park Allocation", value: parkAllocation, highlight: true },
      { label: "Ride Strategy", value: rideStrategy, highlight: true },
      { label: "Lightning Lane", value: lightningLane },
      ...(vqRides.length
        ? [
            {
              label: "Virtual Queue Required",
              value: vqRides.join(", "),
              urgent: true,
              highlight: true,
            },
          ]
        : []),
      ...(llRides.length
        ? [
            {
              label: "Lightning Lane Priority",
              value: llRides.join(", "),
              highlight: true,
            },
          ]
        : []),
      // Show all rides organized by park with detailed view option
      ...(Object.values(ridesByPark).some(park => park.length > 0)
        ? [
            {
              label: "Selected Rides",
              value: `${Object.values(ridesByPark).flat().length} rides selected`,
              hasDetailView: true,
              detailType: "rides",
              detailData: ridesByPark,
            },
          ]
        : []),
      ...(mustRides.length > 20
        ? [
            {
              label: "Must-Do Rides (Complete List)",
              value: `${mustRides.length} rides selected - Park Completionist approach`,
            },
          ]
        : []),
    ],
  });

  // SECTION 4: Shows & Fireworks
  const showPref = q?.showFireworkStrategy || {};
  const mustShows = Array.isArray(showPref?.mustSeeShows)
    ? showPref.mustSeeShows
    : [];
  const showStrategy = Array.isArray(showPref?.showStrategy)
    ? showPref.showStrategy.join(", ")
    : "—";

  // Organize shows by park
  const showsByPark = { MK: [], EPCOT: [], HS: [], AK: [] };
  mustShows.forEach((show) => {
    if (
      show.includes("Happily Ever After") ||
      show.includes("Festival of Fantasy") ||
      show.includes("Starlight")
    ) {
      showsByPark.MK.push(show);
    } else if (show.includes("Luminous")) {
      showsByPark.EPCOT.push(show);
    } else if (show.includes("Fantasmic")) {
      showsByPark.HS.push(show);
    }
  });

  sections.push({
    title: "Shows & Fireworks",
    subtitle: "Entertainment priorities and timing considerations",
    items: [
      { label: "Show Strategy", value: showStrategy },
      ...(mustShows.length
        ? [
            {
              label: "Must-See Shows",
              value: mustShows.join(", "),
            },
          ]
        : []),
    ],
  });

  // SECTION 5: Client Preferences & Considerations (budget no longer asked; old data ignored)
  const te = q?.timeEnergy || {};
  const startTime =
    Array.isArray(te?.parkStartTime) && te.parkStartTime.length
      ? te.parkStartTime.join(", ")
      : "—";
  const breakStrategy =
    Array.isArray(te?.breakTime) && te.breakTime.length
      ? te.breakTime.join(", ")
      : "—";

  const sc = q?.specialConsiderations || {};
  
  // Celebration - show details (and date if present) if Yes, otherwise show No
  const celebrationDetailsStr = sc?.celebrationDetails?.trim() || "Yes — Did Not Provide Details";
  const celebrationDateStr = sc?.celebrationDate ? (typeof sc.celebrationDate === "string" ? sc.celebrationDate : new Date(sc.celebrationDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })) : "";
  const celebration = sc?.celebration === "Yes"
    ? (celebrationDateStr ? `${celebrationDetailsStr} (${celebrationDateStr})` : celebrationDetailsStr)
    : "No";
  
  // Disabilities/Mobility - show details if Yes, otherwise show No
  const hasDisability = sc?.hasDisability === "Yes" 
    ? (sc?.disabilities || "Yes — Did Not Provide Details") 
    : "No";
  
  // Additional Information - show details if Yes, otherwise show No
  const additionalInfo = sc?.additionalInfo === "Yes" 
    ? (sc?.additionalInfoDetails || "Yes — Did Not Provide Details") 
    : "No";

  sections.push({
    title: "Client Preferences & Considerations",
    subtitle: "Operational preferences and constraints",
    items: [
      { label: "Start Time Preference", value: startTime },
      { label: "Break Strategy", value: breakStrategy },
      { label: "Celebration", value: celebration },
      { label: "Disabilities/Mobility Needs", value: hasDisability },
      { label: "Additional Information", value: additionalInfo },
    ],
  });

  // Generate summary for quick reference
  const summary = `${clientName} • ${tripDuration} days • ${resort} • ${rideStrategy}`;

  return { sections, summary };
};

/**
 * Create a performance-optimized itinerary data cache
 */
export class ItineraryDataCache {
  constructor() {
    this.cache = new Map();
    this.maxSize = 50;
  }

  set(key, data) {
    if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
    });
  }

  get(key) {
    const cached = this.cache.get(key);
    if (!cached) return null;

    // Cache expires after 5 minutes
    if (Date.now() - cached.timestamp > 5 * 60 * 1000) {
      this.cache.delete(key);
      return null;
    }

    return cached.data;
  }

  clear() {
    this.cache.clear();
  }
}

/**
 * Format time for display
 */
export const formatTime = (timeString) => {
  if (!timeString) return '';
  
  // If it's already formatted (contains AM/PM), return as is
  if (typeof timeString === 'string' && (timeString.includes('AM') || timeString.includes('PM'))) {
    return timeString;
  }
  
  // Try to parse and format the time
  try {
    const [hours, minutes] = String(timeString).split(':');
    const hour = parseInt(hours, 10);
    const min = parseInt(minutes, 10);
    if (Number.isNaN(hour) || Number.isNaN(min)) return '';
    if (hour === 0) return `12:${min.toString().padStart(2, '0')} AM`;
    if (hour < 12) return `${hour}:${min.toString().padStart(2, '0')} AM`;
    if (hour === 12) return `12:${min.toString().padStart(2, '0')} PM`;
    return `${hour - 12}:${min.toString().padStart(2, '0')} PM`;
  } catch {
    return '';
  }
};

/**
 * Format minutes-from-midnight (e.g. 540) to display time (e.g. "9:00 AM").
 * Used for segment items that only have startMin / timeMin.
 */
export const formatTimeFromMinutes = (minutes) => {
  if (minutes == null || Number.isNaN(Number(minutes))) return '';
  const m = Number(minutes);
  const hour = Math.floor(m / 60) % 24;
  const min = m % 60;
  if (hour === 0) return `12:${min.toString().padStart(2, '0')} AM`;
  if (hour < 12) return `${hour}:${min.toString().padStart(2, '0')} AM`;
  if (hour === 12) return `12:${min.toString().padStart(2, '0')} PM`;
  return `${hour - 12}:${min.toString().padStart(2, '0')} PM`;
};

/**
 * Get activity time
 */
export const getActivityTime = (activity) => {
  if (!activity) return '';
  
  // Check for time property
  if (activity.time) return formatTime(activity.time);
  
  // Check for scheduledTime property
  if (activity.scheduledTime) return formatTime(activity.scheduledTime);
  
  // Check for startTime property
  if (activity.startTime) return formatTime(activity.startTime);
  
  // Segment items often have startMin or timeMin (minutes from midnight) but no time string
  const minutes = activity.startMin ?? activity.timeMin ?? activity.__anchorMin;
  if (minutes != null && !Number.isNaN(Number(minutes))) {
    return formatTimeFromMinutes(minutes);
  }
  
  return '';
};

/**
 * Get activity duration
 */
export const getActivityDuration = (activity) => {
  if (!activity) return '';
  
  // Check for duration property
  if (activity.duration) return activity.duration;
  
  // Check for estimatedDuration property
  if (activity.estimatedDuration) return activity.estimatedDuration;
  
  // Default durations based on activity type
  if (activity.type === 'show') return '20 minutes';
  if (activity.type === 'dining') return '60 minutes';
  if (activity.type === 'ride') return '5 minutes';
  
  return '';
};

/**
 * Get activity location
 */
export const getActivityLocation = (activity) => {
  if (!activity) return '';
  
  // Check for location property
  if (activity.location) return activity.location;
  
  // Check for zone property
  if (activity.zone) return activity.zone;
  
  // Check for area property
  if (activity.area) return activity.area;
  
  return '';
};

/** Display zone for UI - never show "Unknown Zone", use "Zone TBD" instead. */
export const getDisplayZone = (zoneOrLocation) => {
  const z = zoneOrLocation?.location ?? zoneOrLocation;
  if (!z) return null;
  return z === "Unknown Zone" ? "Zone TBD" : z;
};

/**
 * Get startMin (minutes from midnight) for an activity.
 * Prefers startMin if present (backend canonical); otherwise parses time string.
 */
export const getActivityStartMin = (activity) => {
  if (!activity) return null;
  if (typeof activity.startMin === "number" && !Number.isNaN(activity.startMin)) {
    return activity.startMin;
  }
  const timeStr = activity?.time || activity?.scheduledTime || activity?.startTime;
  if (!timeStr || typeof timeStr !== "string") return null;
  const m = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  const period = (m[3] || "").toUpperCase();
  if (period === "PM" && h !== 12) h += 12;
  if (period === "AM" && h === 12) h = 0;
  return h * 60 + min;
};

/**
 * Ensure activities are in monotonic time order. If decreasing startMin detected, re-sort and warn (dev).
 * @param {object[]} items - Activities (object or string)
 * @param {string} contextLabel - e.g. "2025-02-05 morning" for diagnostics
 * @returns {object[]} Same array or newly sorted copy
 */
export const ensureMonotonicSchedule = (items, contextLabel) => {
  if (!Array.isArray(items) || items.length <= 1) return items;
  const getMin = (x) => getActivityStartMin(x) ?? Number.POSITIVE_INFINITY;
  for (let i = 1; i < items.length; i++) {
    const p = getMin(items[i - 1]);
    const c = getMin(items[i]);
    if (typeof p === "number" && typeof c === "number" && c < p) {
      if (typeof process !== "undefined" && process.env?.NODE_ENV === "development") {
        console.warn(`[Itinerary] Non-monotonic order in ${contextLabel}; resorting defensively`);
      }
      return sortActivitiesByTime(items);
    }
  }
  return items;
};

/**
 * Sort activities by time. Defensive: Transfer before non-transfer when times equal.
 * Prevents "time travel" display. Returns NEW array. Post-sort monotonic check: if out-of-order, re-sort and warn (dev).
 */
export const sortActivitiesByTime = (activities) => {
  if (!Array.isArray(activities)) return [];
  const arr = [...activities];
  const getMin = (x) => getActivityStartMin(x) ?? Number.POSITIVE_INFINITY;
  const isTransfer = (x) => x?.type === "transfer" || (x?.type === "note" && String(x?.name || "").startsWith("Transfer"));
  arr.sort((a, b) => {
    const ma = getMin(a);
    const mb = getMin(b);
    if (ma !== mb) return ma - mb;
    return (isTransfer(a) ? 0 : 1) - (isTransfer(b) ? 0 : 1);
  });
  for (let i = 1; i < arr.length; i++) {
    const p = getMin(arr[i - 1]);
    const c = getMin(arr[i]);
    if (typeof p === "number" && typeof c === "number" && c < p) {
      if (typeof process !== "undefined" && process.env?.NODE_ENV === "development") {
        console.warn(
          "[sortActivitiesByTime] Monotonic violation:",
          arr[i]?.name,
          c,
          "before",
          arr[i - 1]?.name,
          p,
          "- re-sorting"
        );
      }
      arr.sort((a, b) => {
        const ma = getMin(a);
        const mb = getMin(b);
        if (ma !== mb) return ma - mb;
        return (isTransfer(a) ? 0 : 1) - (isTransfer(b) ? 0 : 1);
      });
      break;
    }
  }
  return arr;
};


export default {
  safeJsonParse,
  normalizeItineraryData,
  extractGuestInfo,
  validateAndFormatDate,
  extractArrivalTime,
  cleanActivities,
  transformItineraryData,
  analyzeDayType,
  getDiningInfo,
  getActivitiesByTimeBlock,
  getAllRides,
  getAllShows,
  hasDiningInfo,
  getSpecialtyExperiences,
  getAllSpecialtyExperiences,
  hasSpecialtyExperiences,
  validateItineraryData,
  processAgentNotes,
  ItineraryDataCache,
  formatTime,
  getActivityTime,
  getActivityDuration,
  getActivityLocation,
  getActivityStartMin,
  ensureMonotonicSchedule,
  sortActivitiesByTime,
  getDisplayZone,
  dedupeOverflowNotes,
};